import React, { type ReactNode } from 'react';
import { keyframes, styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { FullPageLayout } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import type { SkeletonConfig } from './types.tsx';

export const defaultSkeletonConfig: SkeletonConfig = [];

export const Skeleton = ({
	skeletonConfig = defaultSkeletonConfig,
}: {
	skeletonConfig?: SkeletonConfig;
}) => (
	<>
		{skeletonConfig.map(({ width }, index) => (
			<ForgeSkeleton data-testId={`forgeskeleton-${index}`} key={index} width={width} />
		))}
	</>
);

type LayoutWrapperProps = {
	children: ReactNode;
	layout?: FullPageLayout;
};

export const SkeletonLayout = ({ children, layout }: LayoutWrapperProps) => {
	if (!layout) return <>{children}</>;
	return (
		<Box testId="forge-ui-async.common.ui.skeleton.box" xcss={layoutStyles}>
			{children}
		</Box>
	);
};

const shimmer = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '2000px 0',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ForgeSkeleton = styled.div<{
	width: string | number;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ width }) => `${width}%`,
	height: token('space.200'),
	marginTop: token('space.200'),
	animationDuration: '1s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationName: shimmer,
	animationTimingFunction: 'linear',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.skeleton'),
	backgroundImage: `linear-gradient( to right, ${token('color.skeleton')} 10%, ${token('color.skeleton.subtle')} 30%, ${token('color.skeleton')} 50% )`,
	backgroundRepeat: 'no-repeat',
});

const layoutStyles = xcss({
	paddingTop: 'space.200',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
});
